import React, { useCallback, useState, useEffect } from "react";
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Button, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  MenuItem, 
  Chip, 
  Select, 
  Snackbar, 
  Alert, 
  Tooltip,
  InputAdornment, 
  IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AiOutlineCloudUpload, AiOutlineShareAlt } from "react-icons/ai";
import { MdAutoFixHigh } from "react-icons/md";
import { FaFacebookF, FaLinkedinIn, FaRedditAlien, FaTelegramPlane, FaWhatsapp } from "react-icons/fa";
import { SiX } from 'react-icons/si'
import DashboardHeader from "../../components/common/DashboardHeader/DashboardHeader";
import DashboardFooter from "../../components/common/DashboardFooter/DashboardFooter";
import Sidebar from "../../components/common/Sidebar/Sidebar";
import Memory from "../../components/common/Memory/Memory";
import styles from "./CreateMemory.module.css";
import heic2any from "heic2any";

import { apiFetch, API_BASE } from "../../utils/api";
import * as Sentry from "@sentry/react";

const CreateMemory = () => {
  const navigate = useNavigate();
  const [currentState, setCurrentState] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [MemoryData, setMemoryData] = useState({
    title: "",
    description: "",
    price: "",
    amount: "",
    tags: [],
    art: null
  });
  const maxStates = 4;

  const [ tweetText, setTweetText ] = useState('');
  const [ tweetBaseText, setTweetBaseText ] = useState('');
  const [ tweetUrl, setTweetUrl ] = useState('');


  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  const handleMenuClick = () => setIsSidebarVisible(prev => !prev);
  const handleNavItemClick = () => {
    if (window.innerWidth <= 768) setIsSidebarVisible(false);
  };

  const predefinedTags = ["Sport", "Art", "Music", "NFT", "Photography", "Video", "Exclusive", "Live Event", "Moment", "Certified"];

  const handleNextClick = useCallback(() => {
    if (currentState < maxStates) {
      setCurrentState((prevState) => prevState + 1);
    } else {
      navigate("/dashboard");
    }
    window.scrollTo({ top: 0, behavior: 'smooth' }); 
  }, [navigate, currentState]);

  const resizeImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
  
      reader.onload = function (event) {
        const img = new Image();
        img.onload = function () {
          let width = img.width;
          let height = img.height;
  
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height = height * (maxWidth / width);
              width = maxWidth;
            } else {
              width = width * (maxHeight / height);
              height = maxHeight;
            }
          }
  
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
  
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const resizedFile = new File([blob], file.name, {
                  type: "image/jpeg",
                  lastModified: Date.now(),
                });
                resolve(resizedFile);
              } else {
                reject(new Error("Canvas toBlob failed"));
              }
            },
            "image/jpeg",
            quality
          );
        };
        img.onerror = reject;
        img.src = event.target.result;
      };
  
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    let ext = file.name?.split(".").pop()?.toLowerCase() || "";
    let type = file.type || "";
  
    // Fallback for camera uploads with no type or extension
    if (!type && !ext) {
      // Use FileReader to check if it's an image
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (result?.startsWith("data:image/")) {
          type = "image/jpeg";
          ext = "jpg";
          finalize(file, type, ext);
        } else {
          Sentry.captureMessage("❌ Unknown camera file type");
          alert("Unsupported file format. Try uploading from gallery.");
        }
      };
      reader.readAsDataURL(file);
      return;
    }
  
    if (!type && ext === "heic") type = "image/heic";
  
    let finalFile = file;
  
    // Convert HEIC to JPEG
    if (ext === "heic" || type === "image/heic") {
      try {
        const blob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.9,
        });
  
        const convertedBlob = Array.isArray(blob) ? blob[0] : blob;
        finalFile = new File([convertedBlob], file.name.replace(/\.heic$/i, ".jpg"), {
          type: "image/jpeg",
          lastModified: new Date().getTime(),
        });
  
        console.log("✅ HEIC converted to JPEG:", finalFile);
      } catch (err) {
        Sentry.captureException(err);
        console.error("❌ HEIC conversion failed:", err);
        alert("Failed to convert HEIC. Try a different image.");
        return;
      }
    }

    // 🔧 Resize image if over 5MB and is resizable type
    const resizableTypes = ["image/jpeg", "image/png"];
    if (resizableTypes.includes(finalFile.type) && finalFile.size > 5 * 1024 * 1024) {
      try {
        console.log("🔄 Resizing large image...");
        finalFile = await resizeImage(finalFile);
      } catch (err) {
        console.error("❌ Failed to resize image:", err);
        alert("Image resize failed.");
        return;
      }
    }
  
    finalize(finalFile, finalFile.type, ext);
  };
  
  const finalize = (file, type, ext) => {
    const validTypes = ["image/png", "image/jpeg", "image/gif", "image/webp", "video/mp4", "audio/mp3"];
  
    if (validTypes.includes(type) && file.size <= 5 * 1024 * 1024) {
      const artURL = URL.createObjectURL(file);
      setSelectedFile(file);
      setMemoryData(prevData => ({
        ...prevData,
        art: artURL,
      }));
      console.log("📦 Selected file ready:", file);
    } else {
      Sentry.captureMessage("Invalid file type or size", {
        extra: { type, ext, file },
      });
      alert("Invalid file type or file too large. Please upload a valid file.");
    }
  };

  const handleBackClick = () => {
    if (currentState > 1) {
      setCurrentState((prevState) => prevState - 1);
    }
  };

  const progressWidth = `${(currentState / maxStates) * 100}%`;

  const Step1 = () => {
    const hasUploadedImage = !!MemoryData.art;

    return (
      <>
        <Box className={styles.uploadSection}>
          <Box className={styles.texts}>
            <Typography className={styles.heading}>Upload an item</Typography>
            <Box className={styles.description}>
              <Typography>
                Upload your Memory file. Supported formats: PNG, JPG, GIF, WebP, MP4, MP3. Maximum file size: 100 Mb.
              </Typography>
            </Box>
          </Box>

          <Box 
            className={styles.uploadBox} 
            style={MemoryData.art ? { backgroundImage: `url(${MemoryData.art})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
          >
            {!hasUploadedImage && (
              <label htmlFor="file-upload" className={styles.uploadArea}>
                <AiOutlineCloudUpload className={styles.uploadIcon} />
                <Typography className={styles.uploadPrompt}>Drag or click to upload your item</Typography>
                <Typography className={styles.fileTypes}>
                  PNG, JPG, GIF, WebP, MP4 or MP3. Maximum file size 100 Mb.
                </Typography>
                
              </label>
            )}
            <input 
              id="file-upload" 
              type="file" 
              accept="image/*,video/*,audio/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            {hasUploadedImage && (
              <Button 
                className={styles.changeButton} 
                onClick={() => document.getElementById('file-upload').click()}
                startIcon={<AiOutlineCloudUpload />}
              >
                  Change
              </Button>
            )}
          </Box>
        </Box>

        <Box className={styles.buttonContainer1}>
          <Button 
            className={styles.nextButton} 
            disabled={!selectedFile} 
            onClick={handleNextClick}>
            Next
          </Button>
        </Box>
      </>
    );
  };

  const Step2 = () => {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [price, setPrice] = useState(1.99);
    const [amount, setAmount] = useState(0);
    const [tags, setTags] = useState([]);
    const [loadingTitle, setLoadingTitle] = useState(false); 
    const [loadingDescription, setLoadingDescription] = useState(false); 

    const [openGenerateModal, setOpenGenerateModal] = useState(false);
    const [modalInitialText, setModalInitialText] = useState("");
    const [modalInstructions, setModalInstructions] = useState("");
    const [modalTarget, setModalTarget] = useState(null); 
    const [modalType, setModalType] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);

    const [errors, setErrors] = useState({
      title: "",
      description: "",
      price: "",
      amount: ""
    });

    useEffect(() => {
      if (MemoryData?.title && !title) setTitle(MemoryData.title);
      if (MemoryData?.description && !description) setDescription(MemoryData.description);
      if (MemoryData?.price && price === 1.99) setPrice(MemoryData.price);
      if (MemoryData?.amount && amount === 0) setAmount(MemoryData.amount);
      if (MemoryData?.tags?.length && tags.length === 0) setTags(MemoryData.tags);
    }, []);
  
    const handleTagSelect = (event) => {
      const value = event.target.value;
      if (!tags.includes(value)) {
        setTags((prevTags) => [...prevTags, value]);
      }
    };
  
    const handleTagDelete = (tagToDelete) => {
      setTags((prevTags) => prevTags.filter((tag) => tag !== tagToDelete));
    };
  
    const allFieldsPopulated = title && description && price;

    const handleValidateAndContinue = () => {
      const newErrors = {
        title: title.trim() ? "" : "Title is required",
        description: description.trim() ? "" : "Description is required",
        price: !price ? "Price is required" : price > 10000000 ? "Max price is 10,000,000" : "",
        amount: amount > 1000000 ? "Max amount is 1,000,000" : ""
      };
    
      setErrors(newErrors);
    
      const hasError = Object.values(newErrors).some(Boolean);
      if (hasError) return;
    
      saveAndContinue();
    };

    const saveAndContinue = () => {
        setMemoryData(prevData => ({
            ...prevData,
            title: title,
            description: description,
            price: price,
            amount: amount,
            tags: tags
        }));
        handleNextClick();
    };

    const SaveAndBack = () => {
      setMemoryData(prevData => ({
          ...prevData,
          title: title,
          description: description,
          price: price,
          amount: amount,
          tags: tags
      }));
      handleBackClick();
    }

    const handleOpenGenerateModal = (currentText, targetSetter, aiType) => {
      setModalInitialText(currentText);
      setModalInstructions((prev) => prev || "Make it more engaging and inspirational");
      setModalType(aiType)
      setModalTarget(() => targetSetter);
      setOpenGenerateModal(true);
    };

    const handleCloseGenerateModal = () => {
      setOpenGenerateModal(false);
    };
    
    const handleConfirmGenerateModal = async () => {
      setIsGenerating(true);
      try {
        const res = await apiFetch("/api/generate/text", {
          method: "POST",
          body: JSON.stringify({
            initialText: modalInitialText,
            type: modalType, // or "heading", if you want dynamic you can add a modalType state
            instructions: modalInstructions,
          }),
        }, true);
    
        if (res.generated && modalTarget) {
          modalTarget(res.generated);
        }
      } catch (err) {
        Sentry.captureException(err);
        console.error("❌ Failed to generate improved text:", err);
      } finally {
        setIsGenerating(false);
        setOpenGenerateModal(false);
      }
    };
  
    return (
      <>
        <Box className={styles.stepSection}>
          <Typography className={styles.heading}>Enter memory details</Typography>
          <Typography className={styles.subText}>
            Provide key information about your memory.
          </Typography>
  
          <Box className={styles.form}>
            <TextField
              label="Name your item"
              variant="standard"
              value={title}
              error={!!errors.title}
              helperText={errors.title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Help create text with AI">
                      <span>
                        <IconButton
                          onClick={() => handleOpenGenerateModal(title, setTitle, "heading")}
                          disabled={loadingTitle}
                        >
                          {loadingTitle ? (
                            <CircularProgress size={20} />
                          ) : (
                            <MdAutoFixHigh />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
  
            <Box className={styles.inputGroup}>               
                <TextField
                    label="Set item price"
                    variant="standard"
                    value={price}
                    error={!!errors.price}
                    helperText={errors.price}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d{0,2}$/.test(value)) {
                        setPrice(value);
                        }
                    }}
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    inputMode="decimal"
                    InputProps={{
                        startAdornment: <span style={{ marginRight: "8px" }}>$</span>
                    }}
                    onBlur={() => {
                        if (price && !isNaN(price)) {
                        setPrice(parseFloat(price).toFixed(2));
                        }
                    }}
                />
  
                <TextField
                    label="Amount of memories (unlimited if set to 0)"
                    variant="standard"
                    value={amount}
                    error={!!errors.amount}
                    helperText={errors.amount}
                    onChange={(e) => {
                        const value = e.target.value;

                        // Allow only integers (no decimals)
                        if (/^\d*$/.test(value)) {
                        setAmount(value);
                        }
                    }}
                    type="number"
                    fullWidth
                    inputProps={{ min: 0, step: 1 }}
                    />
            </Box>
  
            <TextField
              label="Enter short description"
              variant="standard"
              value={description}
              error={!!errors.description}
              helperText={errors.description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={4}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Help create text with AI">
                      <span>
                        <IconButton
                          onClick={() => handleOpenGenerateModal(description, setDescription, "description")}
                          disabled={loadingDescription}
                        >
                          {loadingDescription ? (
                            <CircularProgress size={20} />
                          ) : (
                            <MdAutoFixHigh />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
  
            <Box className={styles.tagInput}>
              <Typography variant="subtitle1">Add tags</Typography>
              <Select
                value=""
                displayEmpty
                onChange={handleTagSelect}
                variant="standard"
                fullWidth
              >
                <MenuItem value="" disabled>Select Tag</MenuItem>
                {predefinedTags.map((tag) => (
                  <MenuItem key={tag} value={tag}>{tag}</MenuItem>
                ))}
              </Select>
  
              <Box className={styles.selectedTags}>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleTagDelete(tag)}
                    className={styles.tag}
                  />
                ))}
              </Box>
            </Box>
          </Box>
          
          <Box className={styles.buttonContainer}>
            <Button className={styles.backButton} onClick={SaveAndBack}>Back</Button>
            <Button 
              className={styles.nextButton} 
              onClick={handleValidateAndContinue}
            >
              Next
            </Button>
          </Box>
        </Box>

        <Dialog open={openGenerateModal} onClose={handleCloseGenerateModal} maxWidth="sm" fullWidth>
          <DialogTitle>Improve your text with AI</DialogTitle>

          <DialogContent>
            <TextField
              label="Initial Text"
              value={modalInitialText}
              onChange={(e) => setModalInitialText(e.target.value)}
              multiline
              rows={4}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Instructions"
              value={modalInstructions}
              onChange={(e) => setModalInstructions(e.target.value)}
              multiline
              rows={4}
              fullWidth
              margin="normal"
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseGenerateModal} disabled={isGenerating}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmGenerateModal}
              disabled={isGenerating}
              variant="contained"
              sx={{ backgroundColor: "#ff9500", color: "#fff", textTransform: "none", borderRadius: "50px" }}
            >
              {isGenerating ? "Generating..." : "OK"}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  const Step3 = () => {
    const { art, description, title, price, tags, amount } = MemoryData;
    const todayDate = new Date().toLocaleDateString();

    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [filePath, setFilePath] = useState("");

    const uploadFile = async () => {
      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
  
        const uploadResponse = await fetch(`${API_BASE}/api/upload-memory`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: formData,
        });
  
        if (!uploadResponse.ok) {
          setErrorMessage("File upload failed.");
          return null;
        }
  
        const uploadResult = await uploadResponse.json();
        setFilePath(uploadResult.url);
        return uploadResult.url;
      } catch (err) {
        const details = {
          message: "Upload fetch failed",
          url: `${API_BASE}/api/upload-memory`,
          fileName: selectedFile?.name,
          fileType: selectedFile?.type,
          fileSize: selectedFile?.size,
          tokenPresent: !!localStorage.getItem("authToken"),
        };
      
        Sentry.captureException(err, {
          extra: details,
        });
      
        console.error("❌ File upload error:", err, details);
        setErrorMessage("File upload error.");
        return null;
      }
    };
  
    const createMemory = async (filePathUrl) => {
      try {
        const response = await apiFetch(
          "/api/create-memory",
          {
            method: "POST",
            body: JSON.stringify({
              title,
              description,
              price: parseFloat(price),
              amount: parseInt(amount),
              tags,
              file_path: new URL(filePathUrl).pathname,
            }),
          },
          true
        );
  
        if (!response || !response.uuid) {
          setErrorMessage("Memory creation failed.");
          return null;
        }
  
        return response.uuid;
      } catch (err) {
        Sentry.captureException(err);
        console.error("❌ Memory creation error:", err);
        setErrorMessage("Memory creation error.");
        return null;
      }
    };
  
    const generateTweet = async (uuid) => {
      try {
        const response = await apiFetch(
          "/api/generate/tweet",
          {
            method: "POST",
            body: JSON.stringify({ uuid }),
          },
          true
        );
  
        if (response?.tweet) {
          setTweetText(`${response.tweet} ${response.link}`);
          setTweetBaseText(response.tweet);
          setTweetUrl(response.link);
        }
      } catch (err) {
        Sentry.captureException(err);
        console.error("❌ Failed to generate tweet:", err);
      }
    };
  
    const handlePublish = async () => {
      setIsProcessing(true);
      setErrorMessage("");
  
      try {
        let finalPath = filePath;
  
        if (!finalPath) {
          finalPath = await uploadFile();
          if (!finalPath) return;
        }
  
        const uuid = await createMemory(finalPath);
        if (!uuid) return;
  
        await generateTweet(uuid);
  
        handleNextClick();
      } finally {
        setIsProcessing(false);
      }
    };

    return (
      <>
        <Box className={styles.stepSection}>
          <Typography className={styles.heading}>Ready to Publish</Typography>
  
          <Box className={styles.memoryContainer}>
            <Box className={styles.memoryColumn}>
              <Memory
                art={art || "/bg@2x.png"}
                description={description}
                title={title}
                price={`${price}`}
                memoryDate={todayDate}
                id={"temp-id"}
                descriptor={"Memory Preview"}
                initialLikeCount={0}
                userLiked={false}
                showBuyButton={false}
                fullPath={true}
                available={amount}
                all={amount}
              />
            </Box>

            <Box className={styles.dataColumn}>
              <Box className={styles.dataGrid}>
                <Typography variant="h6" className={styles.dataTitle}>Verify Your Details</Typography>
                <Box className={styles.dataRow}>
                    <Box className={styles.dataLabel}>Title:</Box>
                    <Box className={styles.dataValue}>{title}</Box>
                </Box>
                <Box className={styles.dataRow}>
                    <Box className={styles.dataLabel}>Description:</Box>
                    <Box className={styles.dataValue}>{description}</Box>
                </Box>
                <Box className={styles.dataRow}>
                    <Box className={styles.dataLabel}>Price:</Box>
                    <Box className={styles.dataValue}>${price}</Box>
                </Box>
                <Box className={styles.dataRow}>
                    <Box className={styles.dataLabel}>Amount:</Box>
                    <Box className={styles.dataValue}>{amount === "0" ? "Unlimited" : amount}</Box>
                </Box>
                <Box className={styles.dataRow}>
                    <Box className={styles.dataLabel}>Tags:</Box>
                    <Box className={styles.dataValue}>{tags.join(", ") || "None"}</Box>
                </Box>
                <Box className={styles.dataRow}>
                    <Box className={styles.dataLabel}>Date:</Box>
                    <Box className={styles.dataValue}>{todayDate}</Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Buttons */}
          {isProcessing ? (
            <Box className={styles.buttonContainer}>
              <Box className={styles.loadingContainer}>
                <CircularProgress size={24} sx={{ color: "#ff9500" }} />
                <Typography className={styles.processingText} sx={{ marginTop: "10px" }}>Processing...</Typography>
              </Box>
            </Box>
          ) : (
            <Box className={styles.buttonContainer}>
              <Button className={styles.backButton} onClick={handleBackClick}>Back</Button>
              <Button 
                className={styles.nextButton} 
                onClick={handlePublish}
              >
                Publish
              </Button>
            </Box>
          )}
          
        </Box>

        <Snackbar
          open={!!errorMessage}
          autoHideDuration={4000}
          onClose={() => setErrorMessage("")}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setErrorMessage("")}>
            {errorMessage}
          </Alert>
        </Snackbar>
      </>
    );
  };

  const Step4 = () => {
    const { art, description, title, price, tags, amount } = MemoryData;
    const todayDate = new Date().toLocaleDateString();
    const [showSnackbar, setShowSnackbar] = useState(false);


    const handleCopyTweet = () => {
        navigator.clipboard.writeText(tweetText);
        setShowSnackbar(true);
    };

    const handleCopyMemoryURL = () => {
      navigator.clipboard.writeText(tweetUrl);
      setShowSnackbar(true);
  };

    const handleShare = (platform) => {
      const url = window.location.href;
      const textFull = encodeURIComponent(tweetText);
      const textBase = encodeURIComponent(tweetBaseText);
      const textUrl = encodeURIComponent(tweetUrl);
      const shareMap = {
        twitter: `https://twitter.com/intent/tweet?text=${textFull}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${textUrl}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${textUrl}`,
        whatsapp: `https://api.whatsapp.com/send?text=${textBase}%20${textUrl}`,
        telegram: `https://t.me/share/url?url=${textUrl}&text=${textBase}`,
        reddit: `https://www.reddit.com/submit?url=${textUrl}&title=${encodeURIComponent(title)}`
      };
      const link = shareMap[platform];
      if (link) window.open(link, '_blank');
    };

    return (
      <>
        <Box className={styles.memoryAndDataContainer}>
          
          {/* Memory Column */}
          <Box className={styles.memoryColumn}>
            <Memory
              art={art || "/bg@2x.png"}
              description={description}
              title={title}
              price={`${price}`}
              memoryDate={todayDate}
              id={"temp-id"}
              descriptor={"Memory Preview"}
              initialLikeCount={0}
              userLiked={false}
              showBuyButton={false}
              fullPath={true}
              available={amount}
              all={amount}
            />
          </Box>

          {/* Data Column */}
          <Box className={styles.dataColumn}>
            <Typography variant="h4" className={styles.dataTitle}>Your Moment is live! <br></br>Share it with your fans now!</Typography>
            
            <Box className={styles.tweetSection}>
              <Typography variant="subtitle1">
                💡 Boost Your Reach! Here's a Tweet Suggestion Crafted Just for You:
              </Typography>
              <TextField
                multiline
                rows={4}
                value={tweetText}
                variant="outlined"
                fullWidth
              />

              <Box className={styles.shareButtonsGrid}>
                <Box className={styles.shareRowMain}>
                  <Button className={styles.copyButton} onClick={handleCopyMemoryURL} startIcon={<AiOutlineShareAlt />}>
                    Copy Memory URL
                  </Button>
                  <Button className={styles.copyButton} onClick={handleCopyTweet} startIcon={<AiOutlineShareAlt />}>
                    Copy Text
                  </Button>
                </Box>

                <Typography className={styles.orShareDivider}>
                  <span className={styles.line} />
                  or share on:
                  <span className={styles.line} />
                </Typography>

                <Box className={styles.shareRow}>
                  <Button className={styles.xButton} onClick={() => handleShare("twitter")} startIcon={<SiX />}>
                    Share on X
                  </Button>
                  <Button className={styles.fbButton} onClick={() => handleShare("facebook")} startIcon={<FaFacebookF />}>
                    Facebook
                  </Button>
                  <Button className={styles.linkedinButton} onClick={() => handleShare("linkedin")} startIcon={<FaLinkedinIn />}>
                    LinkedIn
                  </Button>
                  <Button className={styles.whatsappButton} onClick={() => handleShare("whatsapp")} startIcon={<FaWhatsapp />}>
                    WhatsApp
                  </Button>
                  <Button className={styles.telegramButton} onClick={() => handleShare("telegram")} startIcon={<FaTelegramPlane />}>
                    Telegram
                  </Button>
                  <Button className={styles.redditButton} onClick={() => handleShare("reddit")} startIcon={<FaRedditAlien />}>
                    Reddit
                  </Button>
                </Box>
              </Box>
            </Box>
            
            <Button 
              className={styles.closeButton} 
              onClick={handleNextClick}
            >
              Close
            </Button>
          </Box>
        </Box>

        <Snackbar
          open={showSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setShowSnackbar(false)} severity="success" sx={{ width: '100%' }}>
            Text copied to clipboard!
          </Alert>
        </Snackbar>
      </>
    );
  };

  return (
    <Box className={styles.createMemory}>
      <DashboardHeader isCreateMode={true}  onMenuClick={handleMenuClick} />

      <Box className={styles.contentWrapper}>
        <Sidebar
          className={isSidebarVisible ? 'visible' : ''}
          onNavItemClick={handleNavItemClick}
          isVisible={isSidebarVisible}
        />
        {currentState === 1 && <Step1 />}
        {currentState === 2 && <Step2 />}
        {currentState === 3 && <Step3 />}
        {currentState === 4 && <Step4 />}
      </Box>

      <Box className={styles.progressBar}>
        <Box className={styles.progressBackground} />
        <Box className={styles.progressStatus} style={{ width: progressWidth }} />
      </Box>

      <DashboardFooter />
    </Box>
  );
};

export default CreateMemory;